import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import { useCompletion } from 'ai/react';

import {
  Box,
  TextField,
  Typography,
  IconButton,
  Chip,
  Link,
  InputAdornment,
  useTheme,
  Snackbar,
  Alert,
  Collapse,
  CircularProgress,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';

import {
  Search as SearchIcon,
  X as ClearIcon,
  Sparkles as AutoAwesomeIcon,
  X as CloseIcon,
  List as ListIcon,
  ExternalLink as OpenInNewIcon,
  ArrowUp,
} from 'lucide-mui';

import { fetchFiles } from '../../librarySlice';

import { RootState } from '../../../../reducers';
import { getPromptText, SUGGESTIONS } from './utils';
import {
  MultiAskAIProps,
  FileType,
  Stage,
  LoadingStage,
  SnackbarState,
  LibraryFile,
} from './types';

import config from '../../../../../config';
import { getToken } from '../../../../lib/localStorageHandler';
import useLimits from '../../../plan/hooks/limits';

import { useQuery, useUpdateQuery } from '../../../../hooks/urlHooks';
import { useTags } from '../../../../hooks/useTags';
import { MULTI_ASK_AI_LAST_OUTPUT_KEY } from '../../../../constants/tags';
import LearnMoreDialog from './components';
import FileList from './FileList';
import Results from './Results';

// Sanitize markdown to fix code block formatting
const sanitizeMarkdown = (markdown: string): string => {
  // Remove wrapping markdown code block if present
  let sanitized = markdown.replace(
    /^```\s*(?:markdown)?\n([\s\S]*?)\n```$/g,
    '$1'
  );
  // Remove any other code blocks
  sanitized = sanitized.replace(/```[\s\S]*?```/g, '');
  // Remove any inline code backticks
  sanitized = sanitized.replace(/`[^`]*`/g, '$1');
  // Remove any remaining backticks
  sanitized = sanitized.replace(/`/g, '');
  // Trim any extra whitespace that might have been left
  return sanitized.trim();
};

const formatTimestamp = (seconds: string): string => {
  const totalSeconds = parseInt(seconds, 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

interface Props extends MultiAskAIProps {
  open?: boolean;
  onClose?: () => void;
}

const MultiAskAI: React.FC<Props> = ({ open = false, onClose = () => {} }) => {
  const { searchQuery: initialSearchTerm = '' } = useQuery();
  const updateQuery = useUpdateQuery();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [stage, setStage] = useState<Stage>('initial');
  const [loadingStage, setLoadingStage] = useState<LoadingStage>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const { files, dispatch } = useSelector((state: RootState) => ({
    files: state.library.files,
    dispatch: useDispatch(),
  }));

  // Auto-select file from URL if present
  useEffect(() => {
    const path = window.location.hash;
    const match = path.match(/\/([^/]+)$/);
    if (match && match[1]) {
      const fileId = match[1];
      // Only select if the file exists in the files data
      if (
        !selectedFiles.includes(fileId) &&
        files.data.some((file) => file._id === fileId)
      ) {
        setSelectedFiles([fileId]);
      }
    }
  }, [files.data]); // Add files.data as dependency to re-run when files are loaded

  const guardLimits = useLimits();
  const [directions, setDirections] = useState('');
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'warning',
  });
  const theme = useTheme();
  const [activeSource, setActiveSource] = useState<{
    fileId?: string;
    timestamp?: string;
  } | null>(null);
  const [citationCount, setCitationCount] = useState<{ [key: string]: number }>(
    {}
  );
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const { getTag, setTag } = useTags();
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [mobileTab, setMobileTab] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const libraryStatus = useSelector((state: RootState) => state.library.status);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [hideReferences, setHideReferences] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const resetCitationCount = useCallback(() => {
    setCitationCount({});
  }, []);

  const { complete, completion, setCompletion, isLoading } = useCompletion({
    initialCompletion: getTag(MULTI_ASK_AI_LAST_OUTPUT_KEY) || '',
    api: `${config.authBaseUrlV2}/proxy/ai/completion`,
    streamProtocol: 'text',
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    onResponse: () => setLoadingStage(null),
    onFinish: (prompt: string, completionResult: string) => {
      console.log('AI Prompt:', prompt);
      console.log('Raw AI Response:', completionResult);
      const sanitizedCompletion = sanitizeMarkdown(completionResult);
      setStage('final');
      setTag({
        key: MULTI_ASK_AI_LAST_OUTPUT_KEY,
        value: sanitizedCompletion,
      });
    },
  });

  const convertFiles = (reduxFiles: FileType[]): LibraryFile[] =>
    reduxFiles.map(({ _id, name, createdAt, textData }) => ({
      _id,
      name,
      createdAt:
        typeof createdAt === 'string' ? createdAt : createdAt.toISOString(),
      textData: {
        transcriptUrl: textData?.transcriptUrl,
      },
    }));

  const showSnackbar = (
    message: string,
    severity: SnackbarState['severity'] = 'warning'
  ) => {
    setSnackbarState({ open: true, message, severity });
  };

  const runAskAI = async () => {
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one file to analyze');
      return;
    }

    try {
      await guardLimits('multipleRecordingAskAI', async () => {
        setStage('processing');
        setCompletion('');
        resetCitationCount();
        setActiveSource(null);
        setShowVideoPlayer(false);

        setLoadingStage('transcripts');

        const newTranscripts: { [key: string]: string } = {};

        try {
          await Promise.all(
            selectedFiles.map(async (fileId) => {
              const file = files.data.find((f) => f._id === fileId);
              if (!file?.textData?.transcriptUrl) return;

              const response = await fetch(file.textData.transcriptUrl);
              const text = await response.text();
              newTranscripts[file._id] = text;
            })
          );

          setLoadingStage('ai');
          const promptText = getPromptText(
            newTranscripts,
            convertFiles(files.data),
            directions
          );
          setLoadingStage('response');
          complete(promptText, { body: { model: 'better' } });
        } catch (error) {
          console.error('Failed to process transcripts:', error);
          setStage('initial');
          setLoadingStage(null);
          showSnackbar(
            'Failed to process transcripts. Please try again.',
            'error'
          );
        }
      });
    } catch (error) {
      console.log('Guard limits reached:', error);
      setLoadingStage(null);
      setDirections('');
    }
  };

  const handleClearSelections = () => {
    setCompletion('');
    setStage('initial');
    setDirections('');
    resetCitationCount();
    setTag({
      key: MULTI_ASK_AI_LAST_OUTPUT_KEY,
      value: '',
    });
  };

  const handleFileSelect = (fileId: string) => {
    setSelectedFiles((prev) => {
      const newSelection = prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId];

      if (newSelection.length > 0) {
        setCompletion('');
        setStage('initial');
      }

      if (newSelection.length >= 10) {
        showSnackbar(
          'You can only select up to 10 recordings at once',
          'error'
        );
        return prev;
      }
      return newSelection;
    });
  };

  const handleKeyPress = async (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' &&
      stage !== 'processing' &&
      selectedFiles.length > 0 &&
      directions.trim() !== ''
    ) {
      event.preventDefault();
      runAskAI();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one recording first');
      return;
    }

    if (stage === 'processing') {
      return;
    }

    setDirections(suggestion);

    // Create a new function that uses the suggestion directly
    const runAskWithSuggestion = async () => {
      if (!suggestion.trim()) return;

      guardLimits('multipleRecordingAskAI', async () => {
        setCompletion('');
        resetCitationCount();
        setActiveSource(null);
        setShowVideoPlayer(false);

        setStage('processing');
        setLoadingStage('transcripts');

        const newTranscripts: { [key: string]: string } = {};

        try {
          await Promise.all(
            selectedFiles.map(async (fileId) => {
              const file = files.data.find((f) => f._id === fileId);
              if (!file?.textData?.transcriptUrl) return;

              const response = await fetch(file.textData.transcriptUrl);
              const text = await response.text();
              newTranscripts[file._id] = text;
            })
          );

          setLoadingStage('ai');
          const promptText = getPromptText(
            newTranscripts,
            convertFiles(files.data),
            suggestion // Use suggestion directly instead of directions
          );
          setLoadingStage('response');
          complete(promptText, { body: { model: 'fast' } });
        } catch (error) {
          console.error('Failed to process transcripts:', error);
          setStage('initial');
          setLoadingStage(null);
          showSnackbar(
            'Failed to process transcripts. Please try again.',
            'error'
          );
        }
      });
    };

    runAskWithSuggestion();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);

    updateQuery({
      searchQuery: searchTerm,
    });

    // Wait for minimum time and status change
    await new Promise((resolve) => setTimeout(resolve, 300));
    await new Promise((resolve) => {
      const interval = setInterval(() => {
        if (libraryStatus !== 'pending') {
          clearInterval(interval);
          resolve(true);
        }
      }, 100);
    });

    setIsSearching(false);
  };

  const handleSourceClick = (fileId: string, timestamp: string) => {
    if (!fileId || !timestamp) return;

    setActiveSource((prev) => {
      // If clicking the same source, just seek to the timestamp
      if (prev?.fileId === fileId && prev.timestamp === timestamp) {
        const mediaElement = document.querySelector(
          'video, audio'
        ) as HTMLMediaElement;
        if (mediaElement) {
          mediaElement.currentTime = parseInt(timestamp, 10);
        }
        return prev;
      }

      // If clicking a different source, update the source and show the player
      setShowVideoPlayer(true);
      return { fileId, timestamp };
    });
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const { scrollHeight, scrollTop, clientHeight } = target;

      if (
        scrollTop + clientHeight >= scrollHeight - 5 && // When user is 5px from bottom
        libraryStatus !== 'pending' && // Not already loading
        files.pagination?.total > files.data.length // More items available
      ) {
        const nextPage = files.pagination.page + 1;
        dispatch(
          fetchFiles({
            page: nextPage,
            limit: 20,
            searchQuery: searchTerm,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          })
        );
      }
    },
    [files, libraryStatus, dispatch, searchTerm]
  );

  const renderSearchBar = () => (
    <Box component="form" onSubmit={handleSearchSubmit} sx={{ width: '100%' }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search recordings..."
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isSearching ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SearchIcon color="action" fontSize="small" />
              )}
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setSearchTerm('');
                  updateQuery({ searchQuery: '' });
                }}
                edge="end"
                disabled={isSearching}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        disabled={isSearching}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.default',
            border: 'none',
            boxShadow: 'none',
            borderRadius: '9999px',
            transition: theme.transitions.create(['background-color']),
            '&:hover': {
              bgcolor: 'background.default',
            },
            '& fieldset': {
              border: 'none',
            },
            '& .MuiInputBase-input': {
              height: '32px',
              minHeight: 'unset',
              padding: '4px 16px',
              paddingRight: '44px',
              lineHeight: '24px',
            },
          },
        }}
      />
    </Box>
  );

  const handleCopyContent = (cleanedText: string): void => {
    if (cleanedText) {
      navigator.clipboard.writeText(cleanedText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const renderFileList = () => (
    <FileList
      files={files}
      selectedFiles={selectedFiles}
      showSelectedOnly={showSelectedOnly}
      onFileSelect={handleFileSelect}
      onScroll={handleScroll}
      libraryStatus={libraryStatus}
      setShowSelectedOnly={setShowSelectedOnly}
      setSelectedFiles={setSelectedFiles}
    />
  );

  const renderInputArea = () => {
    const activeFile =
      activeSource && activeSource.fileId
        ? files.data.find((f) => f._id === activeSource.fileId)
        : null;
    const timestampSeconds = activeSource?.timestamp
      ? parseInt(activeSource.timestamp, 10)
      : 0;

    if (
      showVideoPlayer &&
      activeFile &&
      activeSource?.fileId &&
      activeSource?.timestamp
    ) {
      return (
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'secondary.light',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                pl: 1,
                pr: 0.5,
                height: 48,
                borderBottom: 1,
                borderColor: 'secondary.light',
                bgcolor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Link
                href={`${
                  window.location.pathname.includes('/app') ? '/app/#' : '/#'
                }/library/${activeFile.teamId}/recents/${activeFile._id}`}
                underline="hover"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Typography variant="body2" color="inherit">
                  Source: {activeFile.name} at{' '}
                  {formatTimestamp(activeSource.timestamp)}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
              </Link>
              <IconButton
                size="small"
                onClick={() => {
                  setShowVideoPlayer(false);
                  setActiveSource(null);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ width: '100%', lineHeight: 0 }}>
              {activeFile.streams?.includes('video') ? (
                <Box
                  sx={{
                    position: 'relative',
                    height: '30vh',
                    maxHeight: '30vh',
                    overflow: 'hidden',
                    bgcolor: 'black',
                  }}
                >
                  <video
                    controls
                    autoPlay
                    src={activeFile.url}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      objectFit: 'contain',
                    }}
                    onLoadedMetadata={(e) => {
                      const video = e.target as HTMLVideoElement;
                      video.currentTime = timestampSeconds;
                    }}
                  >
                    <source src={activeFile.url} type="video/mp4" />
                    <track kind="captions" />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              ) : (
                <audio
                  controls
                  autoPlay
                  src={activeFile.url}
                  style={{
                    width: '100%',
                    display: 'block',
                  }}
                  onLoadedMetadata={(e) => {
                    const audio = e.target as HTMLAudioElement;
                    audio.currentTime = timestampSeconds;
                  }}
                >
                  <source src={activeFile.url} type="audio/mp3" />
                  <track kind="captions" />
                  Your browser does not support the audio tag.
                </audio>
              )}
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'secondary.light',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            maxWidth: '800px',
            mx: 'auto',
            width: '100%',
          }}
        >
          <TextField
            fullWidth
            placeholder={
              selectedFiles.length === 0
                ? 'Select recordings to start analyzing...'
                : 'Ask anything about these recordings...'
            }
            value={directions}
            onChange={(e) => setDirections(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={selectedFiles.length === 0 || stage === 'processing'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {stage === ('processing' as Stage) ? (
                    <CircularProgress size={24} color="primary" />
                  ) : (
                    directions && (
                      <IconButton
                        onClick={runAskAI}
                        disabled={stage === 'processing'}
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          right: '8px',
                          zIndex: 1,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: '50%',
                          width: '36px',
                          height: '36px',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.light,
                            '& svg': {
                              transform: 'translateY(-2px)',
                            },
                          },
                        }}
                      >
                        <ArrowUp
                          style={{
                            transition: 'transform 0.2s ease-in-out',
                            color: theme.palette.primary.contrastText,
                            width: '20px',
                            height: '20px',
                          }}
                        />
                      </IconButton>
                    )
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.default',
                border: 'none',
                boxShadow: 'none',
                borderRadius: '9999px',
                transition: theme.transitions.create(['background-color']),
                '&:hover': {
                  bgcolor: 'background.default',
                },
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  height: '40px',
                  minHeight: 'unset',
                  padding: '8px 16px',
                  paddingRight: '44px',
                  lineHeight: '24px',
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  return open ? (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: (th) => `1px solid ${th.palette.divider}`,
      }}
    >
      {/* Mobile Tabs */}
      {isMobile && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'secondary.light',
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            minHeight: 64,
          }}
        >
          <Tabs
            value={mobileTab}
            onChange={(_, newValue) => setMobileTab(newValue)}
            variant="fullWidth"
            sx={{
              flex: 1,
              '& .MuiTab-root': {
                minHeight: 64,
                fontSize: '0.875rem',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem',
                  marginBottom: '4px',
                },
              },
            }}
          >
            <Tab
              icon={<ListIcon />}
              label={`Select Recordings${
                selectedFiles.length ? ` (${selectedFiles.length})` : ''
              }`}
              sx={{
                fontSize: '0.813rem',
              }}
            />
            <Tab
              icon={<AutoAwesomeIcon />}
              label="Ask AI"
              sx={{
                fontSize: '0.813rem',
                ...(selectedFiles.length > 0 && {
                  '& .MuiSvgIcon-root': {
                    animation: 'pulse 2s infinite',
                    color: 'primary.main',
                  },
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                      opacity: 0.7,
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }),
              }}
            />
          </Tabs>
          <IconButton
            onClick={onClose}
            sx={{
              mr: 1,
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
              },
              display: { xs: 'flex', md: 'none' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {/* Left Sidebar */}
        <Box
          sx={{
            width: { xs: '100%', md: '350px' },
            height: { xs: mobileTab === 0 ? '100%' : 0, md: '100%' },
            display: { xs: mobileTab === 0 ? 'flex' : 'none', md: 'flex' },
            bgcolor: 'background.paper',
            flexDirection: 'column',
            flexShrink: 0,
            overflow: 'hidden',
            transition: theme.transitions.create(['height', 'display']),
            borderRight: {
              xs: 'none',
              md: `1px solid ${theme.palette.secondary.light}`,
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              height: 72,
              borderBottom: 1,
              borderColor: 'secondary.light',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {renderSearchBar()}
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {renderFileList()}
          </Box>
        </Box>

        {/* Right Content */}
        <Box
          sx={{
            flex: 1,
            display: { xs: mobileTab === 1 ? 'flex' : 'none', md: 'flex' },
            height: { xs: mobileTab === 1 ? '100%' : 0, md: '100%' },
            flexDirection: 'column',
            bgcolor: 'background.default',
            overflow: 'hidden',
            transition: theme.transitions.create(['height', 'display']),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              height: 72,
              bgcolor: 'background.paper',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: selectedFiles.length === 0 ? 1 : 0,
              borderColor: 'secondary.light',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" color="text.primary">
                {selectedFiles.length === 0
                  ? 'Select recordings to analyze'
                  : `Analyzing ${selectedFiles.length} recording${
                      selectedFiles.length > 1 ? 's' : ''
                    }`}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                },
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Suggestions */}
          <Collapse in={selectedFiles.length > 0} timeout="auto">
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'secondary.light',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflow: 'auto',
                  flexWrap: { xs: 'nowrap', md: 'wrap' },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  pb: { xs: 0.5, md: 0.25 },
                }}
              >
                {SUGGESTIONS.map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    color="secondary"
                    variant={directions === suggestion ? 'filled' : 'outlined'}
                    size="small"
                    clickable
                    disabled={stage === 'processing'}
                    sx={{
                      borderRadius: '16px',
                      flexShrink: 0,
                      height: { xs: '32px', md: '28px' },
                      '& .MuiChip-label': {
                        px: { xs: 2, md: 1.5 },
                      },
                      transition: theme.transitions.create([
                        'background-color',
                        'box-shadow',
                        'color',
                      ]),
                      '&:hover': {
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                      },
                      ...(directions === suggestion && {
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                        '&:hover': {
                          bgcolor: 'secondary.dark',
                        },
                      }),
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Collapse>

          {/* Results Area */}
          <Box
            sx={{
              flex: 1,
              p: 3,
              overflowY: 'auto',
              height: 0,
            }}
          >
            <Results
              completion={completion}
              isLoading={isLoading}
              stage={stage}
              loadingStage={loadingStage}
              selectedFiles={selectedFiles}
              files={files}
              activeSource={activeSource}
              citationCount={citationCount}
              hideReferences={hideReferences}
              copySuccess={copySuccess}
              handleSourceClick={handleSourceClick}
              handleClearSelections={handleClearSelections}
              onCopyContent={handleCopyContent}
              setCitationCount={setCitationCount}
              setHideReferences={setHideReferences}
              setShowLearnMore={setShowLearnMore}
            />
          </Box>

          {/* Input Area */}
          {renderInputArea()}
        </Box>
      </Box>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbarState.severity}>{snackbarState.message}</Alert>
      </Snackbar>
      <LearnMoreDialog
        open={showLearnMore}
        onClose={() => setShowLearnMore(false)}
      />
    </Box>
  ) : null;
};

export default MultiAskAI;
